<script lang="ts">
import type { InputDateProps, InputTimeProps } from '@nuxt/ui';
import type { DatetimeFieldOptions, PrimitiveType } from '#layers/data-model';
import { PRIMITIVE_TYPE_DATE_TIME, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, PRIMITIVE_TYPE_INT32, PRIMITIVE_TYPE_INT64 } from '#layers/data-model';

export const supportedDateTimeFieldTypes = ({ primitiveType }: { primitiveType: PrimitiveType }) => [
	PRIMITIVE_TYPE_DATE_TIME,
	PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE,
	PRIMITIVE_TYPE_INT32,
	PRIMITIVE_TYPE_INT64,
].includes(primitiveType.name)
	? ['datetime']
	: [];

const __DsBlock = {
	icon: 'lucide:calendar-clock',
	name: '$t:blocks.input_datetime.name',
	description: '$t:blocks.input_datetime.description',
	type: 'input',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: supportedDateTimeFieldTypes,
		recommendedDisplays: ['$builtin:DisplayFormattedDateTime'],
	},

	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'granularity',
			type: 'enum',
			values: ['hour', 'minute', 'second'],
		},
		{
			name: 'defaultTimezone',
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
	],
}</script>

<script lang="ts" setup>
import type { Temporal } from 'temporal-polyfill';
import type { InputBlockProps } from '~/types/input-block';
import { useLocale } from '@nuxt/ui/composables';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { calendarDateTimeConverter, isZonedDateTime, nowDateTime, unixTimestampConverter } from '#layers/base';
import { nowInTimezone, resolveTimezone } from '#layers/base/utils/timezone';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';
import { useTimezone } from '~/composables/use-timezone';

export type DateTimeModelValue = Temporal.PlainDateTime | Temporal.ZonedDateTime | number | null;

interface Props extends DatetimeFieldOptions, InputBlockProps, Pick<InputDateProps, 'color' | 'variant' | 'size' | 'disabled'>, Pick<InputTimeProps, 'granularity'> {
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
	granularity: 'minute',
	defaultTimezone: 'local',
});

const model = defineModel<DateTimeModelValue>();

;

const forwarded = reactivePick(props, 'color', 'variant', 'size', 'disabled', 'block', 'granularity', 'readonly');

const { modelValue: m, disabled, disabledNotice, field } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const isTimestamp = computed(() => !!field.value?.type.name && [PRIMITIVE_TYPE_INT32, PRIMITIVE_TYPE_INT64].includes(field.value.type.name));
const isTimezoneType = computed(() => field.value?.type.name === PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE);

/**
 * Current timezone for the datetime value.
 * - For dateTimeWithTimezone: uses defaultTimezone (defaults to user's local timezone)
 * - For other types: always uses 'UTC' for consistency
 */
const currentTimezone = computed(() => {
	if (!isTimezoneType.value) {
		return 'UTC';
	}

	// Backend stores in UTC, display in user's local timezone by default
	const fallbackTz = props.defaultTimezone?.trim() || 'local';
	return resolveTimezone(fallbackTz);
});

const internalModel = computed<Temporal.PlainDateTime | null>({
	get: () => {
		if (!m.value) return null;

		if (isTimestamp.value && typeof m.value === 'number') {
			const timezone = isTimezoneType.value ? currentTimezone.value : 'UTC';
			return unixTimestampConverter.toPlainDateTime(m.value, timezone);
		}

		if (isZonedDateTime(m.value)) {
			return m.value.withTimeZone(currentTimezone.value).toPlainDateTime();
		}

		return m.value as Temporal.PlainDateTime;
	},
	set: (value) => {
		if (!value) {
			m.value = null;
			return;
		}

		if (isTimestamp.value) {
			const timezone = isTimezoneType.value ? currentTimezone.value : 'UTC';
			m.value = unixTimestampConverter.fromPlainDateTime(value, timezone);
			return;
		}

		if (isTimezoneType.value) {
			m.value = value.toZonedDateTime(currentTimezone.value);
			return;
		}

		m.value = value;
	},
});

const { code: locale } = useLocale();

const {
	timezoneLabel,
	tooltipContent,
} = useTimezone({
	timezone: currentTimezone,
	dateTime: internalModel,
	locale,
});

const calendarModel = computed({
	get: () => calendarDateTimeConverter.toCalendar(internalModel.value),
	set: (value) => {
		internalModel.value = calendarDateTimeConverter.toTemporal(value);
	},
});

function setNow() {
	if (isTimezoneType.value) {
		const now = nowInTimezone(currentTimezone.value);
		internalModel.value = now.toPlainDateTime();
	}
	else if (isTimestamp.value) {
		internalModel.value = nowInTimezone('UTC').toPlainDateTime();
	}
	else {
		internalModel.value = nowDateTime();
	}
}
</script>

<template>
	<DFieldGroup>
		<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
			<DInputDate
				v-bind="forwarded"
				v-model="calendarModel"
				class="w-full"
				:aria-readonly="readonly"
			>
				<template #trailing>
					<DPopover
						:content="{ side: 'bottom', align: 'end' }"
						:ui="{
							content: 'p-2',
						}"
					>
						<DButton
							v-if="!readonly"
							color="neutral"
							variant="link"
							data-test-id="input-date-time-calendar"
							icon="lucide:calendar"
							:disabled
						/>

						<template #content>
							<DCalendar v-model="calendarModel" />
							<div class="flex flex-col items-center justify-center">
								<DInputTime v-model="calendarModel" :granularity :readonly />
								<DSeparator class="my-2" />
								<DButton
									v-if="!readonly"
									variant="link"
									data-test-id="input-date-time-now"
									@click="setNow"
								>
									{{ $t('blocks.input_date.now') }}
								</DButton>
							</div>
						</template>
					</DPopover>
				</template>
			</DInputDate>
		</DTooltip>

		<!-- Timezone indicator badge with timezone conversion tooltip -->
		<DTooltip
			v-if="isTimezoneType && tooltipContent"
			:delay-duration="0"
			:text="tooltipContent"
		>
			<DBadge
				color="neutral"
				class="cursor-default"
				data-test-id="input-date-time-badge"
				variant="subtle"
				icon="lucide:globe"
			>
				{{ timezoneLabel }}
			</DBadge>
		</DTooltip>
	</DFieldGroup>
</template>
